import React, { useEffect, useState, useContext, useRef } from "react";
import { pickConfigToSchemaShape } from "../../utils/pick-config-to-schema-shape";
import {
  EXPORT_KIND,
  extractTabImportPayload,
  getConfigImportTabMismatchMessage,
  getHorizontalTabLabel,
  withExportTabMeta,
} from "../../utils/config-tab-export-meta";
import { pruneConfigByParentField } from "../../utils/prune-config-by-parent-field";
import {
    logExport,
    logImport,
    logSaveClient,
    summarizePayload,
} from "../../utils/config-import-export-log";
import ConfigVerticalMenu from "./ConfigVerticalMenu";
import customizationsService from "../../services/api/customizations-service";
import customizationsV2Service from "../../services/api/customizations-v2-service";

import projectsService from "../../services/api/projects-service";
import contentService from "../../services/api/content-service";
import ConfigForm from "./ConfigForm";
import { useHistory } from "react-router-dom";
import formValidations from "../../variables/form-validations";
import Projects from "../projects/Projects";
import QRCode from 'qrcode';
import ReactBSAlert from "react-bootstrap-sweetalert";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import UserContext from "../../UserContext";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Container,
  Spinner,
  Alert,
  Row,
  Col,
  NavItem,
  NavLink,
  Nav,
  TabContent,
  TabPane,
} from "reactstrap";
import useDynamicResources from "./useDynamicResources"; // Adjust path as needed




const Customizations = (props) => {
  const projectId = new URLSearchParams(props.location.search).get('projectid');
  const platform = new URLSearchParams(props.location.search).get('platform');

  const history = new useHistory();
  const version = new URLSearchParams(props.location.search).get('version');
  const context = useContext(UserContext);
  // console.log("Version:",version);
  if (!version) {
    history.push(`/config/v2/appconfigversions?projectid=${projectId}&platform=${platform}`);
  }
  const [projectName, setProjectName] = useState("");
  const [horizontalTab, setHorizontalTab] = useState('');
  const [configSchema, setConfigSchema] = useState(false);
  const [currentTabConfig, setCurrentTabConfig] = useState(false);
  const [savedConfig, setSavedConfig] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showSaveButton, setShowSaveButton] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [fieldStates, setFieldStates] = useState(false);
  const [allFieldStates, setAllFieldStates] = useState(false);
  const [formFields, setFormFields] = useState(false);
  const [currentFormFields, setCurrentFormFields] = useState(false);
  const [qrcodeLink, setQRCodeLink] = useState("");
  const [packageName, setPackageName] = useState("");
  const [showQRPopup, setShowQRPopup] = useState(false);
  
  const [categoriesData, setCategoriesData] = useState({})
  const [parentCategories, setParentCategories] = useState([]);
  const [parentChildrenCategoryMap, setParentChildrenCategoryMap] = useState({});
  const [taxonomyTypes, setTaxonomyTypes] = useState([]);
  const [taxonomyCategoryLookup, setTaxonomyCategoryLookup] = useState({});

  // Use the new custom hook
  const { resourceList, isLoading: isLoadingResources, error: resourcesError, refetchResources } = useDynamicResources(projectId, version, savedConfig?.data?._id, horizontalTab, configSchema);

  const importFileRef = useRef(null);

  const getV2SchemaItemsForActiveTab = () => {
    if (!configSchema || !configSchema.config || !horizontalTab) return null;
    const tab = configSchema.config[horizontalTab];
    return tab && tab.items ? tab.items : null;
  };

  const handleExportV2TabConfig = () => {
    const items = getV2SchemaItemsForActiveTab();
    if (!items || !currentTabConfig) {
      console.warn("[Superadmin config JSON import/export] EXPORT v2-app-customizations skipped", {
        horizontalTab,
        projectId,
        platform,
        version,
      });
      return;
    }
    const core = pickConfigToSchemaShape(items, currentTabConfig);
    const tabLabel = getHorizontalTabLabel(configSchema, horizontalTab);
    const payload = withExportTabMeta(core, {
      kind: EXPORT_KIND.V2_APP,
      tab: horizontalTab,
      platform,
      tabLabel,
      schemaTopKeys: Object.keys(items).sort(),
    });
    const jsonText = JSON.stringify(payload, null, 2);
    logExport("v2-app-customizations", {
      projectId,
      platform,
      version,
      horizontalTab,
      fileName: `v2-app-${platform || "android"}-${horizontalTab}-v${version || ""}-${projectId || "project"}.json`,
      ...summarizePayload(core),
      note: "MongoDB unchanged until Save.",
    });
    const blob = new Blob([jsonText], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `v2-app-${platform || "android"}-${horizontalTab}-v${version || ""}-${projectId || "project"}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleImportV2TabConfig = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    logImport("v2-app-customizations", "file_selected", {
      name: file.name,
      size: file.size,
      projectId,
      horizontalTab,
      platform,
      version,
    });
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const { data, meta } = extractTabImportPayload(parsed);
        logImport("v2-app-customizations", "parsed_ok", {});
        const items = getV2SchemaItemsForActiveTab();
        if (!items) {
          logImport("v2-app-customizations", "aborted", { reason: "schema_not_loaded" }, new Error("Schema not loaded"));
          toast.error("Schema not loaded.");
          return;
        }
        const mismatch = getConfigImportTabMismatchMessage({
          meta,
          data,
          items,
          horizontalTab,
          platform,
          expectedKind: EXPORT_KIND.V2_APP,
          currentTabLabel: getHorizontalTabLabel(configSchema, horizontalTab),
        });
        if (mismatch) {
          logImport("v2-app-customizations", "aborted", { reason: "wrong_tab", meta }, new Error(mismatch));
          toast.error(mismatch);
          return;
        }
        let sanitized = pickConfigToSchemaShape(items, data);
        sanitized = pruneConfigByParentField(sanitized, items);
        logImport("v2-app-customizations", "sanitized_and_pruned_applied_to_form", {
          ...summarizePayload(sanitized),
          note: "MongoDB projectv2appconfigs not updated until Save.",
        });
        setCurrentTabConfig(sanitized);
        toast.success("Import applied. Click Save to persist.");
      } catch (err) {
        logImport("v2-app-customizations", "parse_or_shape_failed", { projectId, horizontalTab }, err);
        toast.error("Invalid JSON file.");
      }
      e.target.value = "";
    };
    reader.onerror = () => {
      logImport("v2-app-customizations", "file_read_failed", { projectId, horizontalTab }, reader.error);
    };
    reader.readAsText(file);
  };

const isObject = (item) => {
  return (item && typeof item === 'object' && !Array.isArray(item));
};

const applyDefaultsDeep = (saved, defaults) => {
  const result = { ...saved };
  for (const key in defaults) {
    if (Object.prototype.hasOwnProperty.call(defaults, key)) {
      if (result[key] === undefined) {
        result[key] = defaults[key];
      } else if (isObject(result[key]) && isObject(defaults[key])) {
        result[key] = applyDefaultsDeep(result[key], defaults[key]);
      }
    }
  }
  return result;
};

  const generateQR = async text => {
    try {
      setQRCodeLink(await QRCode.toDataURL(text, { errorCorrectionLevel: 'H' }));
    } catch (err) {
      console.error(err)
    }
  }

  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab');
  useEffect(() => {
    if (initialTab) {
      setHorizontalTab(initialTab)
    } else {
      setHorizontalTab('app')
    }
  }, [])

  useEffect(() => {
    (async () => {
      let getProjectDetails = await projectsService.getProjectDetails(projectId);
      if (getProjectDetails.status && getProjectDetails.data) {
        setProjectName(getProjectDetails.data.name);
      }
      let getAppsGeneralSettingsDetails = await projectsService.getProjectAppsGeneralSettings(projectId);
      if (getAppsGeneralSettingsDetails.status) {
        if (getAppsGeneralSettingsDetails.data) {
          setPackageName(getAppsGeneralSettingsDetails.data.android_app_package_name);
        }
      }


      let projectData = await projectsService.getPublisherMultiProjects(projectId);
      if (projectData.status) {
        let allProjectData = projectData.data;
        let projectArray = [];
        if (allProjectData.length) {
          allProjectData.forEach(function (projectDetails) {
            projectArray.push({ value: projectDetails.slug, label: `${projectDetails.name} (${projectDetails.slug})` })
          })
          setProjects(projectArray)
        }
      }

    })();
  }, [])

  useEffect(() => {
    if (packageName && savedConfig.data && savedConfig.data.version) {
      generateQR(packageName + "/" + savedConfig.data.version);
      // console.log(packageName+"/"+savedConfig.data.version);
    }
  }, [packageName, savedConfig]);

  const generateDefaultConfigForTab = (schema, tabName) => {
    if (!schema || !schema.config || !schema.config[tabName] || !schema.config[tabName].items) {
      return {};
    }

    const tabSchemaItems = schema.config[tabName].items;
    let defaultConfig = {};

    const buildDefaultConfigRecursive = (items, currentConfigLevel) => {
      for (const key in items) {
        const itemSchema = items[key];
        if (itemSchema.items && typeof itemSchema.items === 'object' && !Array.isArray(itemSchema.items)) { // Nested group
          currentConfigLevel[key] = {};
          buildDefaultConfigRecursive(itemSchema.items, currentConfigLevel[key]);
        } else if (itemSchema.type === 'multi-input-object') {
          currentConfigLevel[key] = itemSchema.default !== undefined ? itemSchema.default : {}; // Default for multi-input-object itself
        } else if (itemSchema.type === 'multi-input') {
          currentConfigLevel[key] = itemSchema.default !== undefined ? itemSchema.default : [];
        } else {
          currentConfigLevel[key] = itemSchema.default !== undefined ? itemSchema.default : "";
          if (itemSchema.type === 'boolean' && itemSchema.default === undefined) {
            currentConfigLevel[key] = false;
          }
        }
      }
    };

    buildDefaultConfigRecursive(tabSchemaItems, defaultConfig);
    return defaultConfig;
  };

  useEffect(() => {
    (async () => {
      if (projectId && horizontalTab) {
        let schema = await customizationsV2Service.getv2AppConfigSchema(platform);
        setConfigSchema(schema.data);
        // console.log(schema)
        if (!(schema.data.config).hasOwnProperty(horizontalTab)) {
          setHorizontalTab('app')
        };

        let config = await customizationsV2Service.getAppSavedConfig(projectId, version,platform);
        if (config.status && Object.keys(config.data).length !== 0) {
          setSavedConfig(config);
          const defaultConfigForTab = generateDefaultConfigForTab(schema.data, horizontalTab);
          let currentConfig;
          if (config.data[horizontalTab]) {
            const savedTabConfig = JSON.parse(config.data[horizontalTab]);
            currentConfig = applyDefaultsDeep(savedTabConfig, defaultConfigForTab);
          } else {
            currentConfig = defaultConfigForTab;
          }
          setCurrentTabConfig(currentConfig);
          let categoryData = await contentService.getAllCategoriesList(projectId);
          if (categoryData.status) {
            let allCategories = categoryData.data;
            setCategoriesData(allCategories);
            let categoryArray = [];
            if (allCategories.length) {
              const categoryArray = [
                ...allCategories.map(categoryDetails => ({
                  value: String(categoryDetails.id),
                  label: categoryDetails.name
                }))
              ];
              setCategories(categoryArray);
              // Build lookup map
              const categoryById = {};
              allCategories.forEach(cat => {
                categoryById[cat.id] = { ...cat };
              });

              // Helper: get top-level ancestor
              const getRootParentId = (category) => {
                let current = category;
                while (current?.parent_id) {
                  current = categoryById[current.parent_id];
                }
                return current?.id;
              };

              // Build parent array and parentToChildren map (flattened descendants)
              const parentCategoriesArray = [];
              const parentToChildrenMap = {};

              allCategories.forEach(category => {
                const categoryItem = {
                  label: category.name,
                  value: String(category.id)
                };

                const isTopLevel = !category.parent_id;
                const rootId = String(getRootParentId(category));

                if (isTopLevel) {
                  parentCategoriesArray.push(categoryItem);
                } else {
                  if (!parentToChildrenMap[rootId]) {
                    parentToChildrenMap[rootId] = [];
                  }
                  parentToChildrenMap[rootId].push(categoryItem);
                }
              });

              setParentCategories(parentCategoriesArray);
              setParentChildrenCategoryMap(parentToChildrenMap);
              // ========== ADDED END ==========
            }
          }
        } else {
          // console.log("Invalid config Version or you can not edit this config version");
        }
      }

    })();
  }, [horizontalTab]);
  useEffect(() => {
    (async () => {
      if (!projectId) return;

      try {
        let taxonomyData = await contentService.getTaxonomyTypesList(projectId);

        if (taxonomyData.status && taxonomyData.data?.taxonomy_types?.length) {
          const rawTaxonomies = taxonomyData.data.taxonomy_types;

          // 1. Taxonomy array for dropdowns
          const taxonomyArray = rawTaxonomies.map(type => ({
            value: type._id,
            label: type.name,
            name: type.slug
          }));

          setTaxonomyTypes(taxonomyArray);

          // 2. Lookup map: taxonomy_id => [ {label, value}, ... ]
          const taxonomyCategoryLookup = {};
          console.log("Categories:", categoriesData);
          if (categoriesData && categoriesData.length) {
            categoriesData.forEach(category => {
              const taxonomyId = category?.taxonomy_type_id?._id;
              if (!taxonomyId) return;

              const categoryItem = {
                label: category.name,
                value: String(category.id)
              };

              if (!taxonomyCategoryLookup[taxonomyId]) {
                taxonomyCategoryLookup[taxonomyId] = [];
              }

              taxonomyCategoryLookup[taxonomyId].push(categoryItem);
            });
          }

          setTaxonomyCategoryLookup(taxonomyCategoryLookup);

        
        }
      } catch (error) {
        console.error("Failed to fetch taxonomy types:", error);
      }
    })();
  }, [horizontalTab, categories]);



  //for setting form fields validations
  useEffect(() => {
    // console.log("horizontol");
    // console.log(currentTabConfig);

    if (configSchema && savedConfig) {

      const processSchemaItems = (items, pathPrefix = [], currentDataLevel, menuStateData, allFormFields, isMultiObjectItem = false, baseMultiObjectPath = '') => {
        for (const key in items) {
          const currentPath = [...pathPrefix, key];
          const fieldId = currentPath.join(':');
          const fieldSchema = items[key];
          let currentVal = currentDataLevel;

          // For direct children of multi-input-object, currentDataLevel is the specific object instance
          if (!isMultiObjectItem && currentDataLevel) {
            for (const part of pathPrefix) {
              if (currentVal && typeof currentVal === 'object' && currentVal.hasOwnProperty(part)) {
                currentVal = currentVal[part];
              } else {
                currentVal = undefined;
                break;
              }
            }
          }
          // For fields inside a multi-object item, currentDataLevel is already the item itself.
          const itemValue = isMultiObjectItem && currentDataLevel ? currentDataLevel[key] : currentVal;


          allFormFields[fieldId] = fieldSchema;
          menuStateData["error_" + fieldId] = fieldStates?.["error_" + fieldId] || 'has-success';
          menuStateData["error_message_" + fieldId] = fieldStates?.["error_message_" + fieldId] || '';

          if (fieldSchema.items && typeof fieldSchema.items === 'object' && !Array.isArray(fieldSchema.items)) { // Nested group
            processSchemaItems(fieldSchema.items, currentPath, isMultiObjectItem ? itemValue : currentDataLevel, menuStateData, allFormFields, isMultiObjectItem, baseMultiObjectPath);
          } else if (fieldSchema.type === 'multi-input-object' && currentVal && currentVal[key]) {
            const multiObjectData = currentVal[key]; // This is the object like { slug1: {...}, slug2: {...} }
            Object.keys(multiObjectData).forEach(objectInstanceKey => {
              const instancePath = [...currentPath, objectInstanceKey];
              // Recursively process the type_items for each object instance
              processSchemaItems(fieldSchema.type_items, instancePath, multiObjectData[objectInstanceKey], menuStateData, allFormFields, true, instancePath.join(':'));
            });
          } else if (fieldSchema.type === 'multi-input' && currentVal && currentVal[key]) {
            const multiInputArray = currentVal[key];
            if (Array.isArray(multiInputArray)) {
              if (key === 'list') console.log("Processing multi-input list:", multiInputArray.length, "items");
              multiInputArray.forEach((item, index) => {
                const itemPath = [...currentPath, index.toString()];
                if (fieldSchema.type_items && fieldSchema.type_items[0]) { // Assuming type_items[0] is the template
                  if (key === 'list') console.log("Recursing for item:", index, itemPath.join(':'));
                  processSchemaItems(fieldSchema.type_items[0], itemPath, item, menuStateData, allFormFields, true, itemPath.join(':'));
                }
              });
            } else {
              if (key === 'list') console.log("multi-input key 'list' found but value is not array:", multiInputArray);
            }
          }
        }
      };

      let fieldStatesData = {}; let formFieldsData = {};
      Object.keys(configSchema).forEach(function (key) {
        Object.keys(configSchema[key]).forEach(function (horizontolmenukey) {
          let menuStateData = {};
          let allFormFields = {};
          var submenu = configSchema[key][horizontolmenukey].items;
          Object.keys(submenu).forEach(function (submenukey) {
            // Call the recursive processor
            const dataForTab = horizontolmenukey === horizontalTab ? currentTabConfig : (savedConfig.data[horizontolmenukey] ? JSON.parse(savedConfig.data[horizontolmenukey]) : generateDefaultConfigForTab(configSchema, horizontolmenukey));
            processSchemaItems(submenu, [], dataForTab, menuStateData, allFormFields);
          });
          fieldStatesData[horizontolmenukey] = menuStateData;
          formFieldsData[horizontolmenukey] = allFormFields;
        })
      })
      setFieldStates(fieldStatesData[horizontalTab]);
      setAllFieldStates(fieldStatesData);
      setFormFields(formFieldsData);
      setCurrentFormFields(formFieldsData[horizontalTab]);
    }
  }, [currentTabConfig])


  const validateMultiInputObjectsOnSave = (config, schema) => {
    const errors = [];

    // Recursive function to find and validate multi-input-object fields
    function findAndValidate(configNode, schemaNode) {
      if (!configNode || !schemaNode) return;

      for (const key in schemaNode) {
        if (Object.prototype.hasOwnProperty.call(schemaNode, key)) {
          const fieldSchema = schemaNode[key];
          const fieldValue = configNode[key];

          if (fieldSchema && fieldSchema.type === 'multi-input-object' && fieldValue && typeof fieldValue === 'object') {
            const actionKeyField = fieldSchema.action_key_field;
            if (!actionKeyField) continue;

            const itemKeys = Object.keys(fieldValue);
            const seenValues = new Set();
            let hasDuplicates = false;

            for (const itemKey of itemKeys) {
              const item = fieldValue[itemKey];
              if (!item || typeof item !== 'object') continue;

              const innerKeyValue = item[actionKeyField];

              // // Validation 1: Key consistency check.
              // if (itemKey !== innerKeyValue) {
              //   errors.push(`Key mismatch in '${fieldSchema.label}': Item key is '${itemKey}' but its configured key is '${innerKeyValue}'. Please correct it.`);
              // }

              // Validation 2: Uniqueness check for the action key field's value.
              if (innerKeyValue && seenValues.has(innerKeyValue)) {
                hasDuplicates = true;
              }
              if (innerKeyValue) {
                seenValues.add(innerKeyValue);
              }
            }

            if (hasDuplicates) {
              console.log(fieldSchema)
              errors.push(`Duplicate slug value found. Please ensure all keys are unique.`);
            }

          } else if (fieldSchema && fieldSchema.items && typeof fieldSchema.items === 'object' && !Array.isArray(fieldSchema.items) && fieldValue) {
            // This is a group of fields, so we recurse.
            findAndValidate(fieldValue, fieldSchema.items);
          }
        }
      }
    }

    findAndValidate(config, schema);
    return errors;
  };

  // console.log(currentFormFields);
  const storeConfig = async () => {
    if (configSchema && configSchema.config && configSchema.config[horizontalTab]) {
      const validationErrors = validateMultiInputObjectsOnSave(currentTabConfig, configSchema.config[horizontalTab].items);
      if (validationErrors.length > 0) {
        validationErrors.forEach(error => toast.error(error));
        return;
      }
    }

    if (validateConfig()) {
      setShowLoader(true);
      setShowSaveButton(false);
      // console.log(currentTabConfig);
      let saveConfig = await customizationsV2Service.storeAppConfig(projectId, version, horizontalTab, currentTabConfig,platform);
      if (!saveConfig.status && saveConfig.error && saveConfig.error.errorCode === 403) {
        context.setIsAuthorized(context);
        logSaveClient("v2-app-customizations", false, { projectId, version, horizontalTab, platform }, saveConfig.error);
      }
      else if (saveConfig.status) {
        logSaveClient("v2-app-customizations", true, {
          projectId,
          version,
          horizontalTab,
          platform,
          ...summarizePayload(currentTabConfig),
          hint: "Server logs: [MongoDB projectv2appconfigs] storeAppConfig v2 saved",
        });
        setTimeout(() => {
          setShowLoader(false)
          setShowSaveButton(true)
          setAlert({ show: true, type: 'success', message: 'Saved Successfully' });
        }, 1000);

        setTimeout(() => {
          setAlert({ show: false, type: '', message: '' });
        }, 3000);
      } else {
        logSaveClient("v2-app-customizations", false, { projectId, horizontalTab, platform }, saveConfig.error || saveConfig);
      }
    }
  }

  const validateConfig = () => {
    let validated = true;
    let firstInvalidFieldId = '';
    
    try {
      Object.keys(currentFormFields).forEach(function (fieldId) {
      let fieldIdArray = fieldId.split(":");
      let fieldValue = '';
        
        try {
          if (fieldIdArray.length == 5) {
        // console.log("5")
        // if(!currentTabConfig[fieldIdArray[0]]){
        //     currentTabConfig[fieldIdArray[0]] = {}
        // }
        // if(!currentTabConfig[fieldIdArray[0]][fieldIdArray[1]]){
        //     currentTabConfig[fieldIdArray[0]][fieldIdArray[1]] ={}
        // }
        // if(!currentTabConfig[fieldIdArray[0]][fieldIdArray[1]][fieldIdArray[2]]){
        //     currentTabConfig[fieldIdArray[0]][fieldIdArray[1]][fieldIdArray[2]] ={}
        // }
        // if(!currentTabConfig[fieldIdArray[0]][fieldIdArray[1]][fieldIdArray[2]][fieldIdArray[3]]){
        //     currentTabConfig[fieldIdArray[0]][fieldIdArray[1]][fieldIdArray[2]][fieldIdArray[3]] ={}
        // }
        fieldValue = currentTabConfig?.[fieldIdArray[0]]?.[fieldIdArray[1]]?.[fieldIdArray[2]]?.[fieldIdArray[3]]?.[fieldIdArray[4]];
      } else if (fieldIdArray.length == 4) {
        // console.log("4")

        // if(!currentTabConfig[fieldIdArray[0]]){
        //     currentTabConfig[fieldIdArray[0]] = {}
        // }
        // if(!currentTabConfig[fieldIdArray[0]][fieldIdArray[1]]){
        //     currentTabConfig[fieldIdArray[0]][fieldIdArray[1]] ={}
        // }
        // if(!currentTabConfig[fieldIdArray[0]][fieldIdArray[1]][fieldIdArray[2]]){
        //     currentTabConfig[fieldIdArray[0]][fieldIdArray[1]][fieldIdArray[2]] ={}
        // }
        fieldValue = currentTabConfig?.[fieldIdArray[0]]?.[fieldIdArray[1]]?.[fieldIdArray[2]]?.[fieldIdArray[3]];
      } else if (fieldIdArray.length == 3) {
            fieldValue = currentTabConfig?.[fieldIdArray[0]]?.[fieldIdArray[1]]?.[fieldIdArray[2]];
          } else if (fieldIdArray.length == 2) {
            fieldValue = currentTabConfig?.[fieldIdArray[0]]?.[fieldIdArray[1]];
          } else {
            fieldValue = currentTabConfig?.[fieldId];
          }
        } catch (e) {
          console.log(`>>> Crash while accessing field value for: ${fieldId}`, e);
          validated = false;
      }

      const isFieldValid = validateConfigField(fieldId, fieldValue, currentFormFields[fieldId], 'form');
      if (!isFieldValid) {
        if (firstInvalidFieldId == '') {
          firstInvalidFieldId = fieldId;
        }
        validated = false;
      }
    });
    } catch (globalError) {
      console.log(">>> CRITICAL ERROR in validateConfig loop:", globalError);
      return false;
    }

    if (!validated && firstInvalidFieldId) {
      setFieldStates({
        ...fieldStates
      });

      console.log(">>> Auto-scroll looking for ID:", firstInvalidFieldId);
      
      const parts = firstInvalidFieldId.split(':');
      if (parts.length >= 2) {
          const verticalTabId = parts[1];
          const tabLink = document.querySelector(`a[href="#${verticalTabId}"]`);
          if (tabLink) {
              tabLink.click();
          }
      }

      setTimeout(() => {
          const errorElement = document.getElementById(firstInvalidFieldId);
          
          if (errorElement) {
              let currentEl = errorElement;
              let accordionsToOpen = [];
              
              while (currentEl) {
                  const accItem = currentEl.closest('.accordion__item');
                  if (!accItem) break;
                  
                  const accButton = accItem.querySelector('.accordion__button');
                  if (accButton && accButton.getAttribute('aria-expanded') !== 'true') {
                      accordionsToOpen.push(accButton);
                  }
                  currentEl = accItem.parentElement; 
              }
              
              accordionsToOpen.reverse().forEach(btn => btn.click());

              setTimeout(() => {
                  errorElement.scrollIntoView({ behavior: "smooth", block: "center" }); 
                  errorElement.focus();
              }, 150);

          } else {
              console.warn(">>> Auto-scroll failed: Element not found in DOM.");
          }
      }, 100);
    }
    
    return validated;
  }
  const validateEmail = (fieldId, fieldValue, fieldDesign, validationType) => {
    let isValid = true;
    if (fieldValue != '' && !formValidations.verifyEmail(fieldValue)) {
      if (validationType == 'form') {
        fieldStates['error_' + fieldId] = 'has-danger';
        fieldStates['error_message_' + fieldId] = 'Please enter a valid email.';
      } else {
        setFieldStates({
          ...fieldStates,
          ['error_' + fieldId]: 'has-danger',
          ['error_message_' + fieldId]: 'Please enter a valid email.',
        });
      }
      isValid = false;
    } else {
      if (validationType == 'form') {
        fieldStates['error_' + fieldId] = 'has-success';
        fieldStates['error_message_' + fieldId] = '';
      } else {
        setFieldStates({
          ...fieldStates,
          ['error_' + fieldId]: 'has-success',
          ['error_message_' + fieldId]: '',
        });
      }
    }
    return isValid;
  }
  const validatePhone = (fieldId, fieldValue, fieldDesign, validationType) => {
    let isValid = true;
    if (fieldValue != '' && !formValidations.verifyPhoneNumber(fieldValue)) {
      if (validationType == 'form') {
        fieldStates['error_' + fieldId] = 'has-danger';
        fieldStates['error_message_' + fieldId] = 'Please enter a valid phone number.';
      } else {
        setFieldStates({
          ...fieldStates,
          ['error_' + fieldId]: 'has-danger',
          ['error_message_' + fieldId]: 'Please enter a valid phone number.',
        });
      }
      isValid = false;
    } else {
      if (validationType == 'form') {
        fieldStates['error_' + fieldId] = 'has-success';
        fieldStates['error_message_' + fieldId] = '';
      } else {
        setFieldStates({
          ...fieldStates,
          ['error_' + fieldId]: 'has-success',
          ['error_message_' + fieldId]: '',
        });
      }
    }
    return isValid;

  }

  const validateConfigField = (fieldId, fieldValue, fieldDesign, validationType) => {
    let isValid = true;
    let fieldIdArray = fieldId.split(":");
    let parentObject;
    if (fieldIdArray.length === 5) {
      parentObject = currentTabConfig[fieldIdArray[0]]?.[fieldIdArray[1]]?.[fieldIdArray[2]]?.[fieldIdArray[3]];
    } else if (fieldIdArray.length === 4) {
      parentObject = currentTabConfig[fieldIdArray[0]]?.[fieldIdArray[1]]?.[fieldIdArray[2]];
    } else if (fieldIdArray.length === 3) {
      parentObject = currentTabConfig[fieldIdArray[0]]?.[fieldIdArray[1]];
    } else if (fieldIdArray.length === 2) {
      parentObject = currentTabConfig[fieldIdArray[0]];
    }

    if (fieldDesign && fieldDesign.parent_field && typeof fieldDesign.parent_field === 'string') {
      if (parentObject && parentObject[fieldDesign.parent_field] !== fieldDesign.parent_value) {
        return isValid;
      }
    }

    // If parent_field is an object (or if string check passed), we proceed to validation.
    {
      // Generic conditional validation
      if (fieldDesign && fieldDesign.conditional_validations && parentObject) {
        fieldDesign.conditional_validations.forEach(validation => {
          const dependencyValue = parentObject[validation.dependency_field];
          if (validation.dependency_values.includes(dependencyValue)) {
            fieldDesign = { ...fieldDesign, ...validation.overrides };
          }
        });
      }

      if (fieldDesign && fieldDesign.type === 'multi-input' && fieldDesign.total_items) {
        if (fieldValue && fieldValue.length < fieldDesign.total_items) {
          if (validationType == 'form') {
            fieldStates['error_' + fieldId] = 'has-danger';
            fieldStates['error_message_' + fieldId] = `Please Select at least ${fieldDesign.total_items} values`;
          } else {
            setFieldStates({
              ...fieldStates,
              ['error_' + fieldId]: 'has-danger',
              ['error_message_' + fieldId]: `Please Select at least ${fieldDesign.total_items} values`,
            });
          }
          isValid = false;
        }
      }
      if (fieldDesign && (fieldDesign.type == 'string' || fieldDesign.type == 'integer' || fieldDesign.type == 'url' || fieldDesign.type == 'enum' || fieldDesign.type == 'file_upload' || fieldDesign.type == 'json') && fieldDesign.required) {
        if (fieldValue == '' || fieldValue === undefined) {
          if (validationType == 'form') {
            fieldStates['error_' + fieldId] = 'has-danger';
            fieldStates['error_message_' + fieldId] = 'This field is required.';
          } else {
            setFieldStates({
              ...fieldStates,
              ['error_' + fieldId]: 'has-danger',
              ['error_message_' + fieldId]: 'This field is required.',
            });
          }
          isValid = false;
        } else {
          if (validationType == 'form') {
            fieldStates['error_' + fieldId] = 'has-success';
            fieldStates['error_message_' + fieldId] = '';
          } else {
            setFieldStates({
              ...fieldStates,
              ['error_' + fieldId]: 'has-success',
              ['error_message_' + fieldId]: '',
            });
          }
        }
      }
      if (isValid && fieldDesign && fieldDesign.type == 'string' && fieldDesign.validations) {

        for (const value of fieldDesign.validations) {
          switch (value) {
            case "email":
              isValid = validateEmail(fieldId, fieldValue, fieldDesign, validationType);
              break;
            case "phone":
              isValid = validatePhone(fieldId, fieldValue, fieldDesign, validationType);
              break;
          }

        }
      }
      if (isValid && fieldDesign && fieldDesign.type == 'json') {
        const strVal = fieldValue === undefined || fieldValue === null ? '' : String(fieldValue);
        if (fieldDesign.required && strVal.trim() === '') {
          if (validationType == 'form') {
            fieldStates['error_' + fieldId] = 'has-danger';
            fieldStates['error_message_' + fieldId] = 'This field is required.';
          } else {
            setFieldStates({
              ...fieldStates,
              ['error_' + fieldId]: 'has-danger',
              ['error_message_' + fieldId]: 'This field is required.',
            });
          }
          isValid = false;
        } else if (strVal.trim() !== '') {
          try {
            JSON.parse(strVal);
            if (validationType == 'form') {
              fieldStates['error_' + fieldId] = 'has-success';
              fieldStates['error_message_' + fieldId] = '';
            } else {
              setFieldStates({
                ...fieldStates,
                ['error_' + fieldId]: 'has-success',
                ['error_message_' + fieldId]: '',
              });
            }
          } catch (err) {
            if (validationType == 'form') {
              fieldStates['error_' + fieldId] = 'has-danger';
              fieldStates['error_message_' + fieldId] = 'Enter valid JSON.';
            } else {
              setFieldStates({
                ...fieldStates,
                ['error_' + fieldId]: 'has-danger',
                ['error_message_' + fieldId]: 'Enter valid JSON.',
              });
            }
            isValid = false;
          }
        } else {
          if (validationType == 'form') {
            fieldStates['error_' + fieldId] = 'has-success';
            fieldStates['error_message_' + fieldId] = '';
          } else {
            setFieldStates({
              ...fieldStates,
              ['error_' + fieldId]: 'has-success',
              ['error_message_' + fieldId]: '',
            });
          }
        }
      }
      if (isValid && fieldDesign && fieldDesign.type == 'url') {
        if (fieldValue && !formValidations.verifyUrl(fieldValue)) {
          if (validationType == 'form') {
            fieldStates['error_' + fieldId] = 'has-danger';
            fieldStates['error_message_' + fieldId] = 'Please enter a valid url.';
          } else {
            setFieldStates({
              ...fieldStates,
              ['error_' + fieldId]: 'has-danger',
              ['error_message_' + fieldId]: 'Please enter a valid url.',
            });
          }
          isValid = false;
        } else {
          if (validationType == 'form') {
            fieldStates['error_' + fieldId] = 'has-success';
            fieldStates['error_message_' + fieldId] = '';
          } else {
            setFieldStates({
              ...fieldStates,
              ['error_' + fieldId]: 'has-success',
              ['error_message_' + fieldId]: '',
            });
          }
        }
      }

      // Integer validation with min/max checks
      if (fieldDesign && fieldDesign.type === 'integer') {
        
        // 1. If it's completely empty, undefined, or null, skip integer validation.
        // (The 'required' check happens earlier, so if it gets here, empty is allowed).
        if (fieldValue === '' || fieldValue === undefined || fieldValue === null) {
          if (isValid) {
            if (validationType == 'form') {
              fieldStates['error_' + fieldId] = 'has-success';
              fieldStates['error_message_' + fieldId] = '';
            } else {
              setFieldStates(prev => ({
                ...prev,
                ['error_' + fieldId]: 'has-success',
                ['error_message_' + fieldId]: '',
              }));
            }
          }
        } 
        // 2. If a value exists, check if it's a valid number format
        else if (!/^-?\d+$/.test(String(fieldValue))) {
          if (validationType == 'form') {
            fieldStates['error_' + fieldId] = 'has-danger';
            fieldStates['error_message_' + fieldId] = 'Please enter a valid number.';
          } else {
            setFieldStates(prev => ({
              ...prev,
              ['error_' + fieldId]: 'has-danger',
              ['error_message_' + fieldId]: 'Please enter a valid number.',
            }));
          }
          isValid = false;
        } 
        // 3. It is a valid number, check min/max limits
        else {
          const numValue = parseInt(fieldValue, 10);
          let specificIntegerError = false;

          // Debug Warning
          if (fieldDesign.minimum !== undefined && fieldDesign.maximum !== undefined && 
             (numValue < fieldDesign.minimum || numValue > fieldDesign.maximum)) {
              console.warn(`⚠️ LIMIT ERROR on ${fieldId}:`, {
                  value: numValue,
                  min: fieldDesign.minimum,
                  max: fieldDesign.maximum
              });
          }

          // Min Check
          if (fieldDesign.minimum !== undefined && fieldDesign.minimum !== null && numValue < fieldDesign.minimum) {
            const minMessage = `Value must be ${fieldDesign.minimum} or greater.`;
            if (validationType == 'form') {
              fieldStates['error_' + fieldId] = 'has-danger';
              fieldStates['error_message_' + fieldId] = minMessage;
            } else {
              setFieldStates(prev => ({
                ...prev,
                ['error_' + fieldId]: 'has-danger',
                ['error_message_' + fieldId]: minMessage,
              }));
            }
            isValid = false;
            specificIntegerError = true;
          }

          // Max Check (Only if Min check passed)
          if (!specificIntegerError && fieldDesign.maximum !== undefined && fieldDesign.maximum !== null && numValue > fieldDesign.maximum) {
            const maxMessage = `Value must be ${fieldDesign.maximum} or less.`;
            if (validationType == 'form') {
              fieldStates['error_' + fieldId] = 'has-danger';
              fieldStates['error_message_' + fieldId] = maxMessage;
            } else {
              setFieldStates(prev => ({
                ...prev,
                ['error_' + fieldId]: 'has-danger',
                ['error_message_' + fieldId]: maxMessage,
              }));
            }
            isValid = false;
            specificIntegerError = true;
          }

          // Final Success State
          if (isValid) {
          if (validationType == 'form') {
            fieldStates['error_' + fieldId] = 'has-success';
            fieldStates['error_message_' + fieldId] = '';
          } else {
            setFieldStates(prev => ({
              ...prev,
              ['error_' + fieldId]: 'has-success',
              ['error_message_' + fieldId]: '',
            }));
          }
          }
        }
      }

      if (isValid && fieldDesign && fieldDesign.required && fieldDesign.type == 'enum') {
        // console.log(fieldDesign.type_items);
        if (fieldValue != '' && !formValidations.checkValueInArray(fieldValue, fieldDesign.type_items, fieldDesign.type)) {
          if (validationType == 'form') {
            fieldStates['error_' + fieldId] = 'has-danger';
            fieldStates['error_message_' + fieldId] = 'Please enter a valid value.';
          } else {
            setFieldStates({
              ...fieldStates,
              ['error_' + fieldId]: 'has-danger',
              ['error_message_' + fieldId]: 'Please enter a valid value.',
            });
          }
          isValid = false;
        } else {
          if (validationType == 'form') {
            fieldStates['error_' + fieldId] = 'has-success';
            fieldStates['error_message_' + fieldId] = '';
          } else {
            setFieldStates({
              ...fieldStates,
              ['error_' + fieldId]: 'has-success',
              ['error_message_' + fieldId]: '',
            });
          }
        }
      }

      // if (isValid && fieldDesign && fieldDesign.source == 'category_list') {
      //   console.log("in handleChange in validate: ", fieldValue);
      //   console.log("in handleChange in validate:", fieldDesign.type_items);
      //   console.log("in handleChange in validate:", fieldDesign);
      //   console.log("in handleChange in validate:", formValidations.checkValueInArray(fieldValue, fieldDesign.type_items, fieldDesign.type));

      //   if (fieldValue != '' && !formValidations.checkValueInArray(fieldValue, fieldDesign.type_items, fieldDesign.type)) {
      //     if (validationType == 'form') {
      //       fieldStates['error_' + fieldId] = 'has-danger';
      //       fieldStates['error_message_' + fieldId] = 'Please enter a valid value.';
      //     } else {
      //       setFieldStates({
      //         ...fieldStates,
      //         ['error_' + fieldId]: 'has-danger',
      //         ['error_message_' + fieldId]: 'Please enter a valid value.',
      //       });
      //     }
      //     isValid = false;
      //   } else {
      //     if (validationType == 'form') {
      //       fieldStates['error_' + fieldId] = 'has-success';
      //       fieldStates['error_message_' + fieldId] = '';
      //     } else {
      //       setFieldStates({
      //         ...fieldStates,
      //         ['error_' + fieldId]: 'has-success',
      //         ['error_message_' + fieldId]: '',
      //       });
      //     }
      //   }
      // }

      return isValid;
    }
  }

  var navItems = [];
  var tabPanes = [];

  Object.keys(configSchema).forEach(function (key) {
    Object.keys(configSchema[key]).forEach(function (configkey) {
      //console.log(configSchema[key][configkey]);
      navItems.push(<NavItem>
        <NavLink
          aria-expanded={
            horizontalTab === configkey
          }
          data-toggle="tab"
          href="#"
          role="tab"
          className={
            horizontalTab === configkey
              ? "active"
              : ""
          }
          onClick={() => {
            history.push({
              search: `?projectid=${projectId}&version=${version}&platform=${platform}&tab=${configkey}`
            });
            setHorizontalTab(configkey)
            let currentConfig;
            if (savedConfig?.data?.[configkey]) {
              currentConfig = JSON.parse(savedConfig.data[configkey]);
            } else {
              // configSchema here is the state variable holding the fetched schema (schema.data)
              currentConfig = generateDefaultConfigForTab(configSchema, configkey);
            }
            setCurrentTabConfig(currentConfig)
            setFieldStates(allFieldStates[configkey])
            setCurrentFormFields(formFields[configkey]);
          }
          }
        >
          {configSchema[key][configkey].label}
        </NavLink>
      </NavItem>);
      var showVerticalMenu = false;
      var submenu = configSchema[key][configkey].items;
      Object.keys(submenu).forEach(function (submenukey) {
        if (submenu[submenukey].items) {
          showVerticalMenu = true;
        }
        //console.log(submenu[submenukey]);
      });
      //console.log(configSchema[key][configkey]);
      tabPanes.push(<TabPane tabId={configkey} role="tabpanel">
        <Row>
          <Col md="12">
            <div className="dflLft">
              <Form action="" className="form-horizontal" method="POST">

                {horizontalTab === configkey ? showVerticalMenu ? <ConfigVerticalMenu
                  projectId={projectId}
                  fieldStates={fieldStates} setFieldStates={setFieldStates}
                  validateConfigField={validateConfigField}
                  categories={categories} projects={projects}
                  setCurrentTabConfig={setCurrentTabConfig} currentTabConfig={currentTabConfig}
                  data={configSchema[key][configkey]} menutype={configkey} 
                  resourceList={resourceList} isLoadingResources={isLoadingResources}
                  parentCategories={parentCategories}
                  parentChildrenCategoryMap={parentChildrenCategoryMap}
                  taxonomyTypes={taxonomyTypes}
                  taxonomyCategoryLookup={taxonomyCategoryLookup}
                  />
                  : <ConfigForm
                    projectId={projectId}
                    fieldStates={fieldStates} setFieldStates={setFieldStates}
                    validateConfigField={validateConfigField}
                    categories={categories} projects={projects} 
                    parentCategories={parentCategories}
                    parentChildrenCategoryMap={parentChildrenCategoryMap}
                    taxonomyTypes={taxonomyTypes}
                    taxonomyCategoryLookup={taxonomyCategoryLookup}
                    setCurrentTabConfig={setCurrentTabConfig} currentTabConfig={currentTabConfig} data={configSchema[key][configkey]} parent={configkey} subparent='' resourceList={resourceList} isLoadingResources={isLoadingResources} /> : ''}

                {showSaveButton ? <Button
                  className="btnBlue btn-round"
                  type="button"
                  onClick={storeConfig}
                >
                  Save {configSchema[key][configkey].label}
                </Button> : ''}

                {showLoader ? <Spinner animation="border" role="status">
                  <span className="sr-only">Loading...</span>
                </Spinner> : ''}

                {alert.show ? <Alert variant={alert.type}>
                  {alert.message}
                </Alert> : ''}


              </Form>
            </div>
          </Col>
        </Row>


      </TabPane>);
    });
  });

  if (!projectId) {
    return (
      <Projects navigate="config/appcustomizations"></Projects>
    );
  } else {

    return (
      <div className="content mt-30">
        {showQRPopup}
        <Row>
          <Col className="col-md-12">
            <Card border="primary" >
              <CardHeader>
                <CardTitle tag="h4" className="heding_tab_h4">Customizations </CardTitle>
                <div align="right">
                  <input
                    ref={importFileRef}
                    type="file"
                    accept="application/json,.json"
                    style={{ display: "none" }}
                    onChange={handleImportV2TabConfig}
                  />
                  <Button
                    type="button"
                    color="secondary"
                    size="sm"
                    className="btn-smb mr-2"
                    onClick={() => {
                      if (!importFileRef.current) return;
                      importFileRef.current.value = "";
                      importFileRef.current.click();
                    }}
                  >
                    Import tab JSON
                  </Button>
                  <Button type="button" color="secondary" size="sm" className="btn-smb" onClick={handleExportV2TabConfig}>
                    Export tab JSON
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col>
                    <CardTitle>Config : {savedConfig ? savedConfig.data.name : ""}</CardTitle>
                    <CardTitle>Project : {projectName}</CardTitle>
                  </Col>
                  <Col>
                    <div style={{
                      float: "right",
                    }}>
                      <img src={qrcodeLink} onClick={() => {
                        setShowQRPopup(

                          <ReactBSAlert
                            title="Scan & enter into debug mode "
                            onCancel={() => { setShowQRPopup(false) }}
                            openAnim={{ name: 'showSweetAlert', duration: 700 }}
                            showConfirm={false}
                            closeOnClickOutside
                          >
                            <img src={qrcodeLink} width="500" height="450">

                            </img>
                          </ReactBSAlert>

                        )
                      }}>

                      </img><br />
                      <Label align="left">Scan & enter into debug mode </Label>
                    </div>
                  </Col>
                </Row>


              </CardBody>
            </Card>
            <Card>
              <CardHeader>
                {/* <CardTitle tag="h4"  className="heding_tab_h4">Customizations of  {savedConfig?savedConfig.data.name:""} of project {projectName}</CardTitle> */}

              </CardHeader>
              {savedConfig ?
                <CardBody>
                  <div className="nav-tabs-navigation">
                    <div className="nav-tabs-wrapper">
                      <Nav id="tabs" role="tablist" tabs>
                        {navItems}

                      </Nav>
                    </div>
                  </div>
                  <TabContent
                    className="text-center"
                    id="my-tab-content"
                    activeTab={horizontalTab}
                  >
                    {tabPanes}
                  </TabContent>
                </CardBody> :
                "Invalid Version or You can't edit this version"}
            </Card>
          </Col>
        </Row>
        <div
          className="full-page-background"
          style={{
            backgroundImage: `url(${require("../../assets/img/bg/jan-sendereks.jpg")})`,
          }}
        />
        <ToastContainer position="bottom-right" autoClose={3000} limit={1} />
      </div>
    );
  }

}

export default Customizations;